import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { buildResultTemplatesManager } from "@coveo/headless";
import generateResultsJSXElements from "components/coveo-headless/controllers/ResultTemplateManager";

const ContentCards = ({ controller, headlessEngine, showMoreText, templates }) => {
	const [state, setState] = useState(controller.state);
	const resultsRef = useRef(null);

	/**
	 * add/remove padding to all cards in last row of grid
	 */
	const toggleSpacingLastRow = useCallback(() => {
		const grid = Array.from(resultsRef.current.children) || [];
		if (grid.length) {
			const cards = grid.filter((g) => g.classList.contains("coveo-headless-card-layout"));
			const lastRow = cards.slice(-1);
			const elementsInLastRow = cards.filter((el) => el.offsetTop === lastRow[0].offsetTop);
			grid.forEach((el) => el.classList.remove("coveo-headless-cards-last-row"));
			elementsInLastRow.forEach((el) => el.classList.add("coveo-headless-cards-last-row"));
		}
	}, []);

	/**
	 * use coveo-headless engine (state.isLoading) to determine
	 * if a new request has been made to obtain unfiltered/filtered results
	 */
	useEffect(() => {
		if (resultsRef.current && !state.isLoading) {
			toggleSpacingLastRow();
		}
	}, [state.isLoading, state.moreResultsAvailable, toggleSpacingLastRow]);

	useLayoutEffect(() => {
		if (resultsRef.current) {
			window.addEventListener("resize", toggleSpacingLastRow);
			return () => {
				window.removeEventListener("resize", toggleSpacingLastRow);
			};
		}
	}, [toggleSpacingLastRow]);

	useEffect(() => {
		controller.subscribe(() => {
			setState(controller.state);
		});
	}, [controller]);

	const resultTemplatesManager = useMemo(
		() => buildResultTemplatesManager(headlessEngine),
		[headlessEngine]
	);
	resultTemplatesManager.registerTemplates(
		...generateResultsJSXElements(templates, headlessEngine)
	);

	const showMoreButton = () => {
		if (state.moreResultsAvailable) {
			if (state.isLoading) {
				return <div className="coveo-headless-loading-spinner"></div>;
			}
			return (
				<button
					className="coveo-headless-results-show-more-button t-label-sm-heavy"
					onClick={() => {
						controller.fetchMoreResults();
					}}
				>
					{showMoreText}
				</button>
			);
		}
		return null;
	};

	return (
		<>
			<div
				className="coveo-headless-card-layout-container"
				data-qa="coveo-headless-card-layout-container"
				ref={resultsRef}
			>
				{state.results.length
					? state.results.map((result) => {
							const template = resultTemplatesManager.selectTemplate(result);
							if (!template) throw new Error(`No result template provided for ${result.title}.`);
							return template(result);
						})
					: null}
			</div>
			{showMoreButton()}
		</>
	);
};

export default ContentCards;
